import { LitElement } from "lit";
import { listenForLangChanged } from "lit-translate";

/**
 * When the language changed request update is called.
 *
 * @example
 * 		@customElement("bs-auth-button")
 * 		@translatedElement
 * 		export class BsAuthButton extends BsFormItem {
 * 				...
 * 		}
 * @param constructor
 */
export function translatedElement<T extends { new (...args: any[]): LitElement }>(constructor: T) {
	return class extends constructor {
		private langChangedListener: (() => void) | undefined;

		connectedCallback() {
			super.connectedCallback();
			this.langChangedListener = listenForLangChanged(this.langChanged.bind(this));
		}

		disconnectedCallback() {
			super.disconnectedCallback();
			this.langChangedListener?.();
		}

		langChanged() {
			this.requestUpdate();
		}
	};
}

/*
USAGE:
	@langChangedListener()
	langChanged() {
		this.featureCollections = ALL_FEATURES();
		this.updateDocumentTitle();
		this.requestUpdate();
	}
*/
export function langChangedListener() {
	return function <T extends LitElement>($target: T, functionName: string, descriptor: PropertyDescriptor): void {
		const onLangChanged = descriptor.value;
		const connectedCallback = $target.connectedCallback;
		const disconnectedCallback = $target.disconnectedCallback;
		let unsubscribe: Function | undefined;

		$target.connectedCallback = function (this: T, ...args: []) {
			unsubscribe = listenForLangChanged(() => onLangChanged.apply(this));
			if (connectedCallback != null) {
				connectedCallback.apply(this, ...args);
			}
		};

		// Unsubscribe when the element disconnects
		$target.disconnectedCallback = function (this: T, ...args: []) {
			unsubscribe?.();
			if (disconnectedCallback != null) {
				disconnectedCallback.apply(this, ...args);
			}
		};
	};
}
